import type { WavetableRuntimeTable } from "../types";
import type { WtWarpMode } from "../types";
import { WAVETABLE_FRAME_SIZE } from "./types";

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** Apply warp to a normalized phase 0..1. */
export function warpPhase(phase: number, mode: WtWarpMode, amount: number): number {
  const a = clamp01(amount);
  let p = ((phase % 1) + 1) % 1;
  if (a <= 0 || mode === "none") {
    return p;
  }
  switch (mode) {
    case "bend": {
      // Ease toward ends as amount rises.
      const shaped = p * p * (3 - 2 * p);
      return p * (1 - a) + shaped * a;
    }
    case "mirror": {
      const mirrored = p < 0.5 ? p * 2 : 2 - p * 2;
      return p * (1 - a) + mirrored * a;
    }
    case "sync": {
      // Retrigger-style compress: multiple cycles in one period.
      const cycles = 1 + a * 7;
      return (p * cycles) % 1;
    }
    default:
      return p;
  }
}

function readFrameSample(samples: Float32Array, frame: number, phase: number, frameCount: number): number {
  const f = Math.min(frameCount - 1, Math.max(0, frame));
  const base = f * WAVETABLE_FRAME_SIZE;
  const pos = ((phase % 1) + 1) % 1 * WAVETABLE_FRAME_SIZE;
  const i0 = Math.floor(pos) % WAVETABLE_FRAME_SIZE;
  const i1 = (i0 + 1) % WAVETABLE_FRAME_SIZE;
  const frac = pos - Math.floor(pos);
  const s0 = samples[base + i0] ?? 0;
  const s1 = samples[base + i1] ?? 0;
  return s0 + (s1 - s0) * frac;
}

/**
 * Sample a wavetable at morph `position` (0..1) and oscillator `phase` (0..1),
 * after optional warp.
 */
export function sampleWavetable(
  table: WavetableRuntimeTable,
  phase: number,
  position: number,
  warpMode: WtWarpMode,
  warpAmount: number,
): number {
  const warped = warpPhase(phase, warpMode, warpAmount);
  const frames = Math.max(1, table.frameCount);
  if (frames === 1) {
    return readFrameSample(table.samples, 0, warped, frames);
  }
  const pos = clamp01(position) * (frames - 1);
  const f0 = Math.floor(pos);
  const f1 = Math.min(frames - 1, f0 + 1);
  const frac = pos - f0;
  const a = readFrameSample(table.samples, f0, warped, frames);
  const b = readFrameSample(table.samples, f1, warped, frames);
  return a + (b - a) * frac;
}
